import { Radio } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import type { LocationConfigRow } from 'api/recoveryAi.types'
import LocationMultiSelect, {
  LocationMultiSelectLabels,
} from 'components/LocationMultiSelect'
import React from 'react'
import StatusPill from 'scenes/RecoveryAI/components/StatusPill'
import SelectedLocationsList from 'scenes/RecoveryAI/Settings/ActiveLocations/SelectedLocationsList'
import { useStyletron } from 'styletron-react'

// Approximate: the Radio control's rendered width + its `pl-xs` label gap,
// so the revealed subset content lines up under the subset LABEL text (not
// the radio dot). Tuned against a live render, not derived from spec math —
// re-check if the Radio primitive's own size ever changes.
const SUBSET_INDENT = '28px'

// Radio `name` (groups the two option cards), namespaced by the caller's
// `instanceId` rather than a module constant, because two pickers can now be
// mounted at once (a Settings pane behind an open config modal). Sharing the
// `name` would merge all four radios into ONE browser radio group, so the
// modal's selection would silently steal the pane's.
const modeRadioName = (instanceId: string) =>
  `active-locations-mode-${instanceId}`

// Structurally identical to ActiveLocationsPane's private `Mode` and
// WizardState.locationMode — declared inline (NOT imported) so the picker stays
// decoupled from either consumer's internal types.
export type LocationMode = 'all' | 'subset'

export interface LocationModePickerLabels {
  /** radiogroup aria-label. */
  groupLabel: string
  /** "All N locations" option title (count already resolved by the consumer). */
  allLabel: string
  /** "A subset of locations" option title. */
  subsetLabel: string
  /** Green pill rendered next to the All option's label. */
  recommendedPillLabel: string
  /** Heading above the selected-locations list (rendered sr-only; kept for AT heading-nav and the multiselect's programmatic name). */
  selectedLocationsLabel: string
  /** Pre-translated strings for the controlled multiselect listbox. */
  multiSelect: LocationMultiSelectLabels
  /** aria-label builder for a row's remove button, e.g. name => `Remove ${name}`. */
  removeAriaLabel: (name: string) => string
}

export interface LocationModePickerProps {
  /** The all-vs-subset choice (controlled). */
  mode: LocationMode
  /** Notified when the merchant switches modes. */
  onModeChange: (mode: LocationMode) => void
  /** All selectable locations (drives the multiselect listbox). */
  locations: LocationConfigRow[]
  /** Controlled selection of enabled location ids (raw ints). */
  selectedIds: number[]
  /** Toggle a location in/out of the subset. */
  onToggle: (id: number) => void
  /** Remove a location from the subset by raw-int id. */
  onRemove: (id: number) => void
  /** The selected rows, in display order — rendered as the removable list. */
  selectedLocations: LocationConfigRow[]
  /**
   * Admin write gate. When false the radios are disabled, the editable
   * multiselect is omitted, and the selected list renders read-only (the
   * read-only-STATE half of the gate — a viewer still SEES which locations are
   * active).
   */
  canManage: boolean
  /**
   * REQUIRED (never optional-with-default): namespaces this instance's radio
   * `name` so a second concurrent mount cannot collide with it. A default
   * would silently reintroduce the collision at whichever call site the
   * author forgot — for a bug whose whole nature is "someone mounted a second
   * instance". One of 'settings' | 'wizard' | 'activation-modal' | 'agent-tuning'.
   */
  instanceId: string
  /** Pre-translated, namespace-owned strings (the picker is i18n-agnostic). */
  labels: LocationModePickerLabels
}

// Stable no-op for the disabled state — an inline `() => {}` would be a new
// function reference on every render (mirrors RadioOptionCard's `noop`).
const noop = () => {}

type CompactModeOptionProps = {
  selected: boolean
  value: string
  name: string
  onChange: () => void
  disabled?: boolean
  ariaLabel: string
  label: string
  /** Rendered as a `StatusPill` next to `label` when present (the "all" option only). */
  recommendedPillLabel?: string
}

/**
 * A single plain radio row — no border/box/background, unlike the shared
 * `RadioOptionCard` (which four OTHER pickers still need boxed). Built on the
 * same `Radio` primitive so the control itself looks identical app-wide; only
 * the surrounding chrome differs. Click-anywhere-to-select and the a11y
 * rationale (div stays non-interactive-role; Radio is the real control) are
 * copied verbatim from `RadioOptionCard`.
 */
const CompactModeOption = ({
  selected,
  value,
  name,
  onChange,
  disabled = false,
  ariaLabel,
  label,
  recommendedPillLabel,
}: CompactModeOptionProps) => {
  const [css] = useStyletron()

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={disabled ? undefined : onChange}
      // `mode-option` is a stable structural marker (not a styling hook) so
      // tests can scope assertions to "this option's row" without coupling to
      // layout classes that may change independently.
      // align-items-center (not -start): the "all" row is taller than the
      // "subset" row (it also carries the RECOMMENDED pill), so centering the
      // radio against the row's actual height is what keeps it aligned with
      // the label text in BOTH rows — a fixed offset tuned to one row's
      // height (`mt-n2`, previously here) misaligns the other.
      className={`mode-option d-flex align-items-center ${css({
        cursor: disabled ? 'not-allowed' : 'pointer',
      })}`}
    >
      <Radio
        value={value}
        name={name}
        checked={disabled ? false : selected}
        disabled={disabled}
        onChange={disabled ? noop : onChange}
        overrides={{
          Input: {
            props: { 'aria-label': ariaLabel },
          },
        }}
      />
      <div className={`pl-xs d-flex align-items-center ${css({ gap: '8px' })}`}>
        <Text.BodyText3 bold>{label}</Text.BodyText3>
        {recommendedPillLabel && (
          <StatusPill tone="recommended">{recommendedPillLabel}</StatusPill>
        )}
      </div>
    </div>
  )
}

/**
 * Shared, fully-controlled all-vs-subset location picker (AI-31 / AI-161).
 * Extracted verbatim from ActiveLocationsPane's render body so the onboarding
 * wizard's Locations stage and the Settings pane render the SAME picker rather
 * than forking it. Presentational only — ZERO data fetching, save, hydrate, or
 * unsaved-changes wiring; each consumer owns that and passes pre-translated
 * `labels`. Reuses the merged CompactModeOption + LocationMultiSelect +
 * SelectedLocationsList verbatim.
 */
const LocationModePicker = ({
  mode,
  onModeChange,
  locations,
  selectedIds,
  onToggle,
  onRemove,
  selectedLocations,
  canManage,
  instanceId,
  labels,
}: LocationModePickerProps) => {
  const [css] = useStyletron()

  const radioName = modeRadioName(instanceId)

  // Subset mode reveals the selected-locations section. Managers get the
  // editable multiselect + removable rows; view-only operators still SEE which
  // locations are active (read-only list, no write affordances). Gating the
  // whole section on canManage would have hidden the active subset from viewers.
  const showSubset = mode === 'subset'

  return (
    <>
      {/* a11y: the radiogroup contains ONLY the two radio options. The subset
          details (combobox, listbox, heading, remove buttons) render OUTSIDE it
          — visual nesting comes from paddingLeft, not DOM nesting — so assistive
          tech doesn't enumerate them as group items and miscount "x of y". */}
      <div
        role="radiogroup"
        aria-label={labels.groupLabel}
        className={`d-flex flex-column ${css({ gap: '12px' })}`}
      >
        <CompactModeOption
          selected={mode === 'all'}
          value="all"
          name={radioName}
          onChange={() => onModeChange('all')}
          disabled={!canManage}
          ariaLabel={labels.allLabel}
          label={labels.allLabel}
          recommendedPillLabel={labels.recommendedPillLabel}
        />
        <CompactModeOption
          selected={mode === 'subset'}
          value="subset"
          name={radioName}
          onChange={() => onModeChange('subset')}
          disabled={!canManage}
          ariaLabel={labels.subsetLabel}
          label={labels.subsetLabel}
        />
      </div>

      {showSubset && (
        <div
          className={`mt-s d-flex flex-column ${css({
            gap: '16px',
            paddingLeft: SUBSET_INDENT,
          })}`}
        >
          {canManage && (
            <LocationMultiSelect
              locations={locations}
              selectedIds={selectedIds}
              onToggle={onToggle}
              labels={labels.multiSelect}
            />
          )}
          {selectedLocations.length > 0 && (
            <div className="d-flex flex-column">
              {/* Real heading so screen-reader heading navigation can find the
                  section (WCAG 1.3.1) — visually hidden, since the target design
                  has no visible heading here. h4: it's a subsection of the pane's
                  own "Active locations" h3 (SettingsPaneChrome), so it must nest
                  a level below, not sit as its sibling. */}
              <Text.BodyText4
                bold
                tag="h4"
                className={`sr-only ${css({ margin: 0 })}`}
              >
                {labels.selectedLocationsLabel}
              </Text.BodyText4>
              <SelectedLocationsList
                locations={selectedLocations}
                onRemove={onRemove}
                labels={{ removeAriaLabel: labels.removeAriaLabel }}
                readOnly={!canManage}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default LocationModePicker
